import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarCheck, CalendarX, LinkIcon } from 'lucide-react';

const API = '/api';
const ADMIN_PASSWORD = 'Daquan22';

/**
 * GoogleCalendarPanel
 *
 * Admin widget shown on the Bookings tab. Lets the owner connect the
 * central HQ Studios Google Calendar once; after that every paid booking
 * auto-creates an event on that calendar (and auto-updates/deletes on
 * reschedule/cancel).
 */
const GoogleCalendarPanel = () => {
  const [status, setStatus] = useState({ loading: true });

  const loadStatus = async () => {
    try {
      const res = await axios.get(`${API}/oauth/calendar/status`, {
        params: { admin_password: ADMIN_PASSWORD },
      });
      setStatus({ loading: false, ...res.data });
    } catch (err) {
      setStatus({ loading: false, error: err?.response?.data?.detail || 'Failed to load status' });
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const handleConnect = async () => {
    try {
      const res = await axios.get(`${API}/oauth/calendar/login`, {
        params: { admin_password: ADMIN_PASSWORD },
      });
      // Same tab navigation — Safari blocks window.open() calls that
      // follow an async awaited call. After Google login, our callback
      // bounces the user back to /admin automatically.
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start Google OAuth');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Calendar? New bookings will stop syncing until you reconnect.')) return;
    try {
      await axios.post(`${API}/oauth/calendar/disconnect`, { admin_password: ADMIN_PASSWORD });
      toast.success('Google Calendar disconnected');
      loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const [syncing, setSyncing] = useState(false);
  const handleBackfill = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/oauth/calendar/backfill`, { admin_password: ADMIN_PASSWORD });
      toast.success(`Added ${r.data.created} bookings to Google Calendar${r.data.failed ? ` (${r.data.failed} failed)` : ''}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const connected = status.connected;

  return (
    <div
      data-testid="gcal-admin-panel"
      className="rounded-xl border border-neutral-800 bg-neutral-950 p-4 flex items-center justify-between gap-3"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
            connected ? 'bg-emerald-500/15 text-emerald-400' : 'bg-neutral-800 text-neutral-400'
          }`}
        >
          {connected ? <CalendarCheck size={20} /> : <CalendarX size={20} />}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-white flex items-center gap-2">
            Google Calendar
            {connected && (
              <span className="text-[10px] uppercase tracking-wide text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2 py-0.5">
                Connected
              </span>
            )}
          </div>
          <div className="text-xs text-neutral-400 truncate">
            {status.loading
              ? 'Checking status...'
              : connected
              ? `Syncing bookings to ${status.owner_email}`
              : status.needs_reconnect
              ? 'Google disconnected the calendar. Tap Connect to turn syncing back on. Missed bookings will be added.'
              : 'Not connected — bookings won’t land on your Google Calendar.'}
          </div>
        </div>
      </div>

      <div className="shrink-0 flex items-center gap-2">
        {connected ? (
          <>
          <button
            data-testid="gcal-backfill-btn"
            onClick={handleBackfill}
            disabled={syncing}
            className="text-xs font-semibold text-emerald-300 px-3 py-1.5 rounded-full border border-emerald-500/30 hover:bg-emerald-500/10 disabled:opacity-50 transition"
          >
            {syncing ? 'Syncing...' : 'Sync all bookings'}
          </button>
          <button
            data-testid="gcal-disconnect-btn"
            onClick={handleDisconnect}
            className="text-xs text-red-400 hover:text-red-300 px-3 py-1.5 rounded-full border border-red-500/30 hover:bg-red-500/10 transition"
          >
            Disconnect
          </button>
          </>
        ) : (
          <button
            data-testid="gcal-connect-btn"
            onClick={handleConnect}
            className="text-xs font-semibold text-black bg-white hover:bg-neutral-200 px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition"
          >
            <LinkIcon size={13} /> Connect Google Calendar
          </button>
        )}
      </div>
    </div>
  );
};

export default GoogleCalendarPanel;
